import { ConsentBasis, ConsentIdType, ConsentIdentifierInput } from '../gql/generated/graphql';
import { CookieKeys, getRawCookie, setCookie } from '../hooks/useCookie';
import {
  denyConsent,
  rudderanalytics,
  waitForAnonymousId,
} from '../services/analytics/rudderstack';
import { ONE_DAY_MS } from './dates';
import { fetchVisitorGeo } from './visitorGeo';

export type PrivacyOptOutValue = {
  optedOut: boolean;
  source: 'button' | 'gpc';
  ts: string;
};

export type VisitorRegion = {
  state: string | null;
  in_scope: boolean;
  resolution_source: 'geolite2' | 'unresolved';
};

const PRIVACY_SCOPE_STATES = ['CA', 'CT'];
// leaseend-lab sends this (le_region cookie, /api/geo region) when the IP has no US state
const REGION_UNRESOLVED = 'XX';
const PRIVACY_OPTOUT_COOKIE_LIFETIME = 395 * ONE_DAY_MS;
const SESSION_OPTOUT_KEY = 'le_privacy_session_optout';

let isOptedOutOnPage = false;

// An unknown or non-US state is in scope (FR-12)
const isStateInScope = (state: string | null) => !state || PRIVACY_SCOPE_STATES.includes(state);

export const resolveVisitorRegion = async (): Promise<VisitorRegion> => {
  const region = getRawCookie(CookieKeys.REGION) || (await fetchVisitorGeo()).region;
  const state = region === REGION_UNRESOLVED ? null : region;
  return {
    state,
    in_scope: isStateInScope(state),
    resolution_source: region ? 'geolite2' : 'unresolved',
  };
};

export const isGpcEnabled = () =>
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

// Read raw: getCookie clears unparseable cookies, and this one is lab's legal opt-out.
// Unparseable counts as a button opt-out, so it holds in every state.
export const getOptOutSource = (): PrivacyOptOutValue['source'] | null => {
  const optOut = getRawCookie(CookieKeys.PRIVACY_OPTOUT);
  if (!optOut) {
    return null;
  }

  try {
    const { optedOut, source } = JSON.parse(optOut) as PrivacyOptOutValue;
    if (optedOut !== true) {
      return null;
    }
    return source === 'gpc' ? 'gpc' : 'button';
  } catch {
    return 'button';
  }
};

export const getOptOutCookieBasis = (): ConsentBasis | null => {
  const source = getOptOutSource();
  if (!source) {
    return null;
  }
  return source === 'gpc' ? ConsentBasis.Gpc : ConsentBasis.Dnss;
};

export const isOptedOut = () => getOptOutSource() !== null;

export const isOptedOutForSession = () => {
  if (isOptedOutOnPage) {
    return true;
  }

  try {
    return sessionStorage.getItem(SESSION_OPTOUT_KEY) === 'true';
  } catch {
    return false;
  }
};

export const markOptedOutForSession = () => {
  isOptedOutOnPage = true;
  denyConsent();
  try {
    sessionStorage.setItem(SESSION_OPTOUT_KEY, 'true');
  } catch {
    // Storage blocked: the opt-out holds until the page reloads
  }
};

export const hasPrivacySignal = () => isGpcEnabled() || isOptedOut();

// A button opt-out holds in every state, gpc only in scope
export const isTrackingDenied = async () => {
  if (isOptedOutForSession() || getOptOutSource() === 'button') {
    return true;
  }
  if (!hasPrivacySignal()) {
    return false;
  }
  return (await resolveVisitorRegion()).in_scope;
};

export const writePrivacyOptOut = (source: PrivacyOptOutValue['source']) => {
  setCookie<PrivacyOptOutValue>(
    CookieKeys.PRIVACY_OPTOUT,
    { optedOut: true, source, ts: new Date().toISOString() },
    PRIVACY_OPTOUT_COOKIE_LIFETIME,
  );
  return isOptedOut();
};

export const getBrowserIdentifiers = async (): Promise<ConsentIdentifierInput[]> => {
  await waitForAnonymousId();

  const ids: Record<string, unknown> = {
    ...rudderanalytics.getUserTraits(),
    [ConsentIdType.AnonymousId]: rudderanalytics.getAnonymousId(),
  };
  return Object.values(ConsentIdType)
    .filter((idType) => ids[idType] != null && String(ids[idType]).trim())
    .map((idType) => ({ id_type: idType, id_value: String(ids[idType]) }));
};
